import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { Loader2 } from "lucide-react";

import Section from "../components/Section";
import { FileUpload } from "../components/FileUpload";
import { useAdministrationAPI } from "../AdministrationAPI";
import { authenticationAtom } from "../State";
import { useModalContext } from "../components/ModalProvider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Properties {}

interface SelectOption {
  key: string;
  text: string;
}

export default function APIPanel(_properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const { ingestData } = useAdministrationAPI();
  const [authentication] = useAtom(authenticationAtom) as any;
  const fieldId = useId();
  const [ingestDataDialogOpen, setIngestDataDialogOpen] = useState(false);
  const [ingestDataResult, setIngestDataResult] = useState() as any;
  const [entityType, setEntityType] = useState("Measurement");
  const [uploadingData, setUploadingData] = useState(false);

  const entityTypeId = `${fieldId}-entity-type`;

  const toggleIngestDataDialogOpen = () => {
    setIngestDataDialogOpen(!ingestDataDialogOpen);
  };

  const entityTypeOptions: SelectOption[] = [
    { key: "Measurement", text: t("apiPanel.telemetryData") },
    { key: "Asset", text: t("apiPanel.assets") },
  ];

  const onDataUpload = async (files: FileList) => {
    setUploadingData(true);

    try {
      const formData = new FormData();

      for (let i = 0; i < files.length; ++i) {
        if (files.item(i) !== null) {
          // @ts-ignore
          formData.append(files.item(i).name, files.item(i));
        }
      }

      setIngestDataResult(
        await ingestData(authentication.customer, entityType, formData)
      );
      toggleIngestDataDialogOpen();
    } catch (error) {
      pushError(error);
    } finally {
      setUploadingData(false);
    }
  };

  return (
    <>
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="min-w-0 max-w-prose text-sm">
            {t("apiPanel.uploadDescription")}
          </p>
          {/* "Learn more" navigated to /private/markup/Daten-Upload.md, which the server reads from
              an object-store bucket that did not survive the move to OTC: the fetch fails with a
              500 whose body is the string "Error accessing file from S3", and the page renders that
              string as its content — a link to an error message dressed as documentation. Hidden
              until the document has a home again; what it was meant to say is in tri-server
              readme/DATA_UPLOAD.md (see tri-server#740). */}
        </div>
        <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
          <Section title={t("apiPanel.uploadTelemetryData")}>
            {/* The guide target is the section: there's no button here, just a
                selection plus a drop zone. */}
            <div data-guide-id="settings.api.ingest" className="flex min-w-0 flex-col gap-4">
              <div className="flex min-w-0 max-w-sm flex-col gap-1.5">
                <Label htmlFor={entityTypeId}>{t("apiPanel.objectType")}</Label>
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger id={entityTypeId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {entityTypeOptions.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <FileUpload onUpload={onDataUpload}></FileUpload>
              {uploadingData && (
                <p
                  role="status"
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                  {t("apiPanel.uploadingData")}
                </p>
              )}
            </div>
          </Section>
        </div>
      </div>

      {/* Ingest Data Result Dialog */}
      <Dialog open={ingestDataDialogOpen} onOpenChange={setIngestDataDialogOpen}>
        <DialogContent
          className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("apiPanel.ingestDataResult")}</DialogTitle>
          </DialogHeader>
          {ingestDataResult ? (
            <div className="flex min-w-0 flex-col gap-3">
              <p className="text-sm">
                {t("apiPanel.totalRecords", { total: ingestDataResult.total })}
              </p>
              <div className="grid grid-cols-[minmax(2.5rem,max-content)_1fr] gap-x-4 gap-y-2 text-sm">
                <span className="font-semibold tabular-nums">
                  {ingestDataResult.inserted}
                </span>
                <span className="min-w-0 break-words">
                  {t("apiPanel.inserted")}
                </span>
                <span className="font-semibold tabular-nums">
                  {ingestDataResult.updated}
                </span>
                <span className="min-w-0 break-words">
                  {t("apiPanel.updated")}
                </span>
                <span className="font-semibold tabular-nums">
                  {ingestDataResult.ignored}
                </span>
                <span className="min-w-0 break-words">
                  {t("apiPanel.ignored")}
                </span>
                <span className="font-semibold tabular-nums">
                  {ingestDataResult.deleted}
                </span>
                <span className="min-w-0 break-words">
                  {t("apiPanel.deleted")}
                </span>
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2 pt-4 sm:gap-0">
            <Button variant="outline" onClick={toggleIngestDataDialogOpen}>
              {t("global.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
